import React from 'react';
import { getClosestWords, getHighlight, MAX_PREVIEW_LENGTH } from '@/lib/frontend/utils/search';
import { SearchIndexItem } from '@/types/search';
import { FuseResult } from 'fuse.js';
import { CommandItem } from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';

type Props = {
  result: FuseResult<SearchIndexItem>;
  query: string;
  tag?: string;
};

export const SearchCommandItem = ({ result, query, tag }: Props) => {
  const titleMatch = result.matches?.find((m) => m.key === 'title');
  const contentMatch = result.matches?.find((m) => m.key === 'content');

  const titleHighlight = titleMatch ? getHighlight(result.item.title, query) : undefined;

  let preview = contentMatch
    ? getHighlight(result.item.content, query)?.textParts
    : result.item.content;

  if (!contentMatch) {
    preview = result.item.content;
    if (preview.length > MAX_PREVIEW_LENGTH * 2) {
      const startIndex = 0;
      const endIndex = MAX_PREVIEW_LENGTH * 2;
      const closest = getClosestWords(preview, startIndex, endIndex);
      preview = `${preview.slice(closest[0], closest[1])}...`;
    }
  }

  return (
    <CommandItem
      key={result.item.id}
      value={result.item.id}
      onSelect={() => {
        if (titleMatch) {
          window.location.href = `${result.item.path}`;
          return;
        }

        const contentHighlight = getHighlight(result.item.content, query);

        if (contentHighlight) {
          const [startIndex, endIndex] = getClosestWords(
            result.item.content,
            contentHighlight.startIndex,
            contentHighlight.endIndex,
          );

          // If we have a content highlight, we can use it to navigate
          // to the specific part of the page.
          window.location.href = `${result.item.path}#:~:text=${encodeURIComponent(
            result.item.content.slice(startIndex, endIndex).trim(),
          )}`;

          return;
        }

        window.location.href = result.item.path;
      }}
    >
      <div className="flex gap-1 flex-col">
        <div className="font-bold">
          {titleHighlight?.textParts || result.item.title}
          {tag && (
            <span className="ml-2">
              <Badge variant="purple">{tag}</Badge>
            </span>
          )}
        </div>
        <div className="text-sm opacity-60">{preview}</div>
      </div>
    </CommandItem>
  );
};
